/**
 * Database Schema for Payment Integration
 *
 * This file documents the Firestore collections and fields added for Stripe payment integration.
 * Use this as a reference when working with payment-related data.
 */

export const COLLECTIONS = {
  PAYMENTS: "payments",
  RESOURCE_PURCHASES: "resourcePurchases",
  REFUND_REQUESTS: "refundRequests",
  TEACHERS: "teachers",
  LESSONS: "lessons",
  RESOURCES: "resources",
  STUDENT_PROFILES: "studentProfiles",
  USERS: "users",
  // Promotional features collections
  USER_PROMOTIONS: "userPromotions",
  PROMOTION_USAGE: "promotionUsage",
  // Notification collections
  NOTIFICATION_QUEUE: "notificationQueue",
  NOTIFICATION_HISTORY: "notificationHistory",
  // Reminder collections
  SCHEDULED_REMINDERS: "scheduledReminders",
  // Badge and Review collections
  BADGES: "badges",
  USER_BADGES: "userBadges",
  REVIEWS: "reviews",
  // Discount code collections
  DISCOUNT_CODES: "discountCodes",
  USER_REVIEW_PROGRESS: "userReviewProgress",
};

export const PAYMENT_SCHEMA = {
  // Collection: payments (enhanced with promotional features)
  payments: {
    paymentIntentId: "string", // Stripe PaymentIntent ID
    type: "string", // 'lesson' | 'resource'
    teacherId: "string", // Reference to teacher
    studentId: "string", // Reference to student
    lessonId: "string", // Reference to lesson (if type is 'lesson')
    resourceId: "string", // Reference to resource (if type is 'resource')
    amount: "number", // Final amount in cents (after promotions)
    originalAmount: "number", // Original amount before discounts
    discountAmount: "number", // Discount amount in cents
    platformFee: "number", // Platform fee in cents (10%)
    teacherAmount: "number", // Teacher's amount in cents
    currency: "string", // Currency code (default: 'usd')
    status: "string", // 'pending' | 'completed' | 'failed'
    promotional: "boolean", // Whether this payment uses promotions
    promotionDetails: "object", // Promotion details if applicable
    paymentMethod: "string", // Payment method or 'promotional_discount' for free
    createdAt: "timestamp",
    completedAt: "timestamp", // When payment was completed
    failedAt: "timestamp", // When payment failed
  },

  // Collection: resourcePurchases
  resourcePurchases: {
    resourceId: "string", // Reference to resource
    teacherId: "string", // Reference to teacher who created the resource
    studentId: "string", // Reference to student who purchased
    paymentId: "string", // Reference to payment document
    purchasedAt: "timestamp",
  },

  // Collection: refundRequests
  refundRequests: {
    paymentId: "string", // Reference to payment document
    reason: "string", // Reason for refund
    status: "string", // 'pending' | 'approved' | 'rejected' | 'processed'
    requestedAt: "timestamp",
    processedAt: "timestamp", // When refund was processed
    adminNotes: "string", // Admin notes about the refund
  },

  // Collection: userPromotions (tracks user's promotional usage)
  userPromotions: {
    // Document ID: userId
    firstLessonUsed: "boolean", // Whether user has used first lesson promotion
    totalPromotionsUsed: "number", // Total number of promotions used
    promotionHistory: "array", // Array of promotion IDs used
    lastPromotionUsedAt: "timestamp", // When last promotion was used
    createdAt: "timestamp",
    updatedAt: "timestamp",
  },

  // Collection: promotionUsage (detailed promotion usage records)
  promotionUsage: {
    userId: "string", // Reference to user
    lessonId: "string", // Reference to lesson
    teacherId: "string", // Reference to teacher
    promotionType: "string", // Type of promotion used
    originalAmount: "number", // Original price before discount
    discountAmount: "number", // Amount discounted
    finalAmount: "number", // Final amount paid
    status: "string", // 'applied' | 'cancelled' | 'refunded'
    appliedAt: "timestamp",
    metadata: "object", // Additional promotion data
  },

  // Additional fields for existing collections:
  teachers: {
    // ... existing fields ...
    stripeAccountId: "string", // Stripe Connect account ID
    stripeAccountStatus: "string", // 'no_account' | 'pending' | 'active'
    lessonPrice: "number", // Price per lesson in dollars
    acceptsPayments: "boolean", // Whether teacher accepts online payments
    payoutFrequency: "string", // 'weekly' | 'monthly' (for future use)
    // Promotional settings
    firstLessonSettings: "object", // First lesson promotion settings
    // ... rest of existing fields ...
  },

  lessons: {
    // ... existing fields ...
    paymentStatus: "string", // 'unpaid' | 'pending' | 'paid' | 'failed'
    paymentId: "string", // Reference to payment document
    price: "number", // Lesson price in dollars
    requiresPayment: "boolean", // Whether payment is required
    // ... rest of existing fields ...
  },

  resources: {
    // ... existing fields ...
    price: "number", // Resource price in dollars (0 for free resources)
    isPaid: "boolean", // Whether this is a paid resource
    purchaseCount: "number", // Number of times purchased
    // ... rest of existing fields ...
  },

  // Collection: studentProfiles
  studentProfiles: {
    userId: "string", // Reference to user document
    onboardingCompleted: "boolean", // Whether onboarding is completed
    primaryInstrument: "string", // Primary instrument of interest
    skillLevel: "string", // 'beginner' | 'intermediate' | 'advanced'
    musicalGoals: "array", // Array of goals: ['learn_songs', 'theory', 'performance', 'composition', 'recording']
    preferredGenres: "array", // Array of preferred genres
    availableHours: "string", // Hours available per week: '1-2' | '3-5' | '6-10' | '10+'
    budgetRange: "string", // Budget range: 'under_50' | '50-100' | '100-200' | '200+'
    learningPreferences: "object", // { online: boolean, inPerson: boolean, groupLessons: boolean, oneOnOne: boolean }
    completedAt: "timestamp", // When onboarding was completed
    createdAt: "timestamp",
    updatedAt: "timestamp",
  },

  // Additional fields for users collection:
  users: {
    // ... existing fields ...
    onboardingStatus: "string", // 'pending' | 'completed' | 'skipped'
    onboardingCompletedAt: "timestamp", // When onboarding was completed
    // ... rest of existing fields ...

    // Subcollection: users/{userId}/settings/notifications
    settings: {
      notifications: {
        email: "object", // Email notification preferences by type
        frequency: "string", // 'immediate' | 'daily' | 'weekly'
        timezone: "string", // User's timezone
        quietHours: "object", // { enabled: boolean, start: string, end: string }
        updatedAt: "timestamp",
      },
    },
  },
};

// Badge and Review Schema
export const BADGE_SCHEMA = {
  // Collection: badges (available badges in the system)
  badges: {
    name: "string", // Badge name (e.g., "Friendly", "Good for Beginners")
    description: "string", // Badge description
    category: "string", // Badge category ("personality", "teaching-style", "expertise")
    icon: "string", // Icon identifier or emoji
    color: "string", // Badge color variant
    isActive: "boolean", // Whether badge is active
    sortOrder: "number", // Sort order for display
    createdAt: "timestamp",
    updatedAt: "timestamp",
  },

  // Collection: userBadges (badges awarded to users)
  userBadges: {
    userId: "string", // Reference to user
    badgeId: "string", // Reference to badge
    awardedBy: "string", // Who awarded the badge (system, admin, review)
    awardedAt: "timestamp",
    reviewId: "string", // Reference to review if awarded through review
    metadata: "object", // Additional data about the awarding
  },

  // Collection: reviews (enhanced reviews with badges)
  reviews: {
    studentId: "string", // Reference to student who wrote review
    teacherId: "string", // Reference to teacher being reviewed
    lessonId: "string", // Reference to lesson
    rating: "number", // Star rating (1-5)
    comment: "string", // Written review
    badges: "array", // Array of badge IDs selected by student
    isAnonymous: "boolean", // Whether review is anonymous
    status: "string", // 'active', 'hidden', 'flagged'
    helpfulCount: "number", // Number of helpful votes
    reportCount: "number", // Number of reports
    createdAt: "timestamp",
    updatedAt: "timestamp",
    // Response from teacher
    teacherResponse: "object", // { comment: string, respondedAt: timestamp }
  },
};

// Notification Schema
export const NOTIFICATION_SCHEMA = {
  // Collection: notificationQueue
  notificationQueue: {
    userId: "string", // Reference to user
    notificationType: "string", // Type of notification (from NOTIFICATION_TYPES)
    data: "object", // Notification data including recipient info and content
    frequency: "string", // 'immediate' | 'daily' | 'weekly'
    scheduledFor: "timestamp", // When to send the notification
    status: "string", // 'queued' | 'processing' | 'sent' | 'failed'
    attempts: "number", // Number of send attempts
    lastAttemptAt: "timestamp", // When last attempt was made
    errorMessage: "string", // Error message if failed
    createdAt: "timestamp",
    updatedAt: "timestamp",
  },

  // Collection: notificationHistory
  notificationHistory: {
    userId: "string", // Reference to user
    notificationType: "string", // Type of notification
    data: "object", // Notification data that was sent
    templateType: "string", // Email template type used
    toEmail: "string", // Email address notification was sent to
    status: "string", // 'sent' | 'failed' | 'bounced' | 'delivered'
    sentAt: "timestamp", // When notification was sent
    deliveredAt: "timestamp", // When notification was delivered (if tracking available)
    errorMessage: "string", // Error message if failed
    emailServiceResponse: "object", // Response from email service (Resend)
    createdAt: "timestamp",
  },

  // Subcollection schema for user notification preferences
  userNotificationSettings: {
    // Document path: users/{userId}/settings/notifications
    email: {
      // Lesson notifications
      lesson_booked: "boolean",
      lesson_cancelled: "boolean",
      lesson_reminder_24h: "boolean",
      lesson_reminder_2h: "boolean",
      lesson_reminder_30m: "boolean",
      meeting_link_added: "boolean",
      lesson_rescheduled: "boolean",

      // Payment notifications
      payment_successful: "boolean",
      payment_failed: "boolean",
      refund_processed: "boolean",
      promotional_discount: "boolean",

      // Communication notifications
      new_message: "boolean",
      conversation_started: "boolean",

      // Account notifications
      welcome: "boolean",
      profile_approved: "boolean",
      account_verification: "boolean",

      // Marketing notifications
      feature_announcement: "boolean",
      seasonal_promotion: "boolean",
      inactive_user_engagement: "boolean",
    },
    frequency: "string", // 'immediate' | 'daily' | 'weekly'
    timezone: "string", // IANA timezone string
    quietHours: {
      enabled: "boolean",
      start: "string", // HH:MM format
      end: "string", // HH:MM format
    },
    updatedAt: "timestamp",
  },

  // Collection: scheduledReminders
  scheduledReminders: {
    lessonId: "string", // Reference to lesson
    studentId: "string", // Reference to student (notification recipient)
    teacherId: "string", // Reference to teacher
    reminderType: "string", // Type of reminder (from NOTIFICATION_TYPES)
    scheduledFor: "timestamp", // When to send the reminder
    status: "string", // 'scheduled' | 'sent' | 'cancelled' | 'failed'
    attempts: "number", // Number of send attempts
    lessonData: "object", // Lesson data for the reminder
    sentAt: "timestamp", // When reminder was sent
    cancelledAt: "timestamp", // When reminder was cancelled
    lastError: "string", // Last error message if failed
    lastAttemptAt: "timestamp", // When last attempt was made
    createdAt: "timestamp",
    updatedAt: "timestamp",
  },
};

// Status constants
export const PAYMENT_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  FAILED: "failed",
};

export const REFUND_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  PROCESSED: "processed",
};

export const STRIPE_ACCOUNT_STATUS = {
  NO_ACCOUNT: "no_account",
  PENDING: "pending",
  ACTIVE: "active",
};

export const LESSON_PAYMENT_STATUS = {
  UNPAID: "unpaid",
  PENDING: "pending",
  PAID: "paid",
  FAILED: "failed",
};

// Onboarding status constants
export const ONBOARDING_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  SKIPPED: "skipped",
};

// Student profile constants
export const SKILL_LEVELS = {
  BEGINNER: "beginner",
  INTERMEDIATE: "intermediate",
  ADVANCED: "advanced",
};

export const MUSICAL_GOALS = {
  LEARN_SONGS: "learn_songs",
  THEORY: "theory",
  PERFORMANCE: "performance",
  COMPOSITION: "composition",
  RECORDING: "recording",
};

export const BUDGET_RANGES = {
  UNDER_50: "under_50",
  RANGE_50_100: "50-100",
  RANGE_100_200: "100-200",
  OVER_200: "200+",
};

export const AVAILABLE_HOURS = {
  RANGE_1_2: "1-2",
  RANGE_3_5: "3-5",
  RANGE_6_10: "6-10",
  OVER_10: "10+",
};

// Promotional feature constants
export const PROMOTION_TYPES = {
  FIRST_LESSON_FREE: "first_lesson_free",
  FIRST_LESSON_DISCOUNT: "first_lesson_discount",
  PERCENTAGE_DISCOUNT: "percentage_discount",
  FIXED_AMOUNT_DISCOUNT: "fixed_amount_discount",
};

export const PROMOTION_STATUS = {
  APPLIED: "applied",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
};

// Notification constants
export const NOTIFICATION_TYPES = {
  // Lesson notifications
  LESSON_BOOKED: "lesson_booked",
  LESSON_CANCELLED: "lesson_cancelled",
  LESSON_REMINDER_24H: "lesson_reminder_24h",
  LESSON_REMINDER_2H: "lesson_reminder_2h",
  LESSON_REMINDER_30M: "lesson_reminder_30m",
  MEETING_LINK_ADDED: "meeting_link_added",
  LESSON_RESCHEDULED: "lesson_rescheduled",
  LESSON_REVIEW_PROMPT: "lesson_review_prompt",

  // Payment notifications
  PAYMENT_SUCCESSFUL: "payment_successful",
  PAYMENT_FAILED: "payment_failed",
  REFUND_PROCESSED: "refund_processed",
  PROMOTIONAL_DISCOUNT: "promotional_discount",

  // Communication notifications
  NEW_MESSAGE: "new_message",
  CONVERSATION_STARTED: "conversation_started",

  // Account notifications
  WELCOME: "welcome",
  PROFILE_APPROVED: "profile_approved",
  ACCOUNT_VERIFICATION: "account_verification",

  // Marketing notifications
  FEATURE_ANNOUNCEMENT: "feature_announcement",
  SEASONAL_PROMOTION: "seasonal_promotion",
  INACTIVE_USER_ENGAGEMENT: "inactive_user_engagement",
};

export const NOTIFICATION_CATEGORIES = {
  CRITICAL: "critical", // Lessons, payments - cannot be disabled
  IMPORTANT: "important", // Account updates, messages
  OPTIONAL: "optional", // Marketing, features
};

export const NOTIFICATION_FREQUENCIES = {
  IMMEDIATE: "immediate",
  DAILY: "daily",
  WEEKLY: "weekly",
};

export const NOTIFICATION_QUEUE_STATUS = {
  QUEUED: "queued",
  PROCESSING: "processing",
  SENT: "sent",
  FAILED: "failed",
};

export const NOTIFICATION_DELIVERY_STATUS = {
  SENT: "sent",
  FAILED: "failed",
  BOUNCED: "bounced",
  DELIVERED: "delivered",
};

// Reminder constants
export const REMINDER_STATUS = {
  SCHEDULED: "scheduled",
  SENT: "sent",
  CANCELLED: "cancelled",
  FAILED: "failed",
};

// Badge and Review constants
export const BADGE_CATEGORIES = {
  PERSONALITY: "personality",
  TEACHING_STYLE: "teaching-style",
  EXPERTISE: "expertise",
};

export const REVIEW_STATUS = {
  ACTIVE: "active",
  HIDDEN: "hidden",
  FLAGGED: "flagged",
};

export const BADGE_AWARD_SOURCES = {
  SYSTEM: "system",
  ADMIN: "admin",
  REVIEW: "review",
};

// Helper functions for schema validation
export const validatePaymentData = (payment) => {
  const required = ["type", "teacherId", "studentId", "amount", "currency"];
  const missing = required.filter((field) => !payment[field]);

  if (missing.length > 0) {
    throw new Error(`Missing required payment fields: ${missing.join(", ")}`);
  }

  if (!["lesson", "resource"].includes(payment.type)) {
    throw new Error('Payment type must be "lesson" or "resource"');
  }

  if (payment.type === "lesson" && !payment.lessonId) {
    throw new Error("Lesson payments must include lessonId");
  }

  if (payment.type === "resource" && !payment.resourceId) {
    throw new Error("Resource payments must include resourceId");
  }

  return true;
};

export const validateStripeAccount = (accountData) => {
  const required = ["stripeAccountId", "stripeAccountStatus"];
  const missing = required.filter((field) => !accountData[field]);

  if (missing.length > 0) {
    throw new Error(`Missing required account fields: ${missing.join(", ")}`);
  }

  if (
    !Object.values(STRIPE_ACCOUNT_STATUS).includes(
      accountData.stripeAccountStatus
    )
  ) {
    throw new Error(
      `Invalid account status: ${accountData.stripeAccountStatus}`
    );
  }

  return true;
};

// Helper functions for student profile validation
export const validateStudentProfile = (profile) => {
  const required = [
    "userId",
    "primaryInstrument",
    "skillLevel",
    "musicalGoals",
  ];
  const missing = required.filter((field) => !profile[field]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required student profile fields: ${missing.join(", ")}`
    );
  }

  if (!Object.values(SKILL_LEVELS).includes(profile.skillLevel)) {
    throw new Error(`Invalid skill level: ${profile.skillLevel}`);
  }

  if (
    !Array.isArray(profile.musicalGoals) ||
    profile.musicalGoals.length === 0
  ) {
    throw new Error("Musical goals must be a non-empty array");
  }

  const invalidGoals = profile.musicalGoals.filter(
    (goal) => !Object.values(MUSICAL_GOALS).includes(goal)
  );
  if (invalidGoals.length > 0) {
    throw new Error(`Invalid musical goals: ${invalidGoals.join(", ")}`);
  }

  return true;
};

// Promotional feature validation functions
export const validatePromotionUsage = (promotionUsage) => {
  const required = [
    "userId",
    "lessonId",
    "teacherId",
    "promotionType",
    "originalAmount",
    "discountAmount",
    "finalAmount",
    "status",
  ];
  const missing = required.filter((field) => !promotionUsage[field]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required promotion usage fields: ${missing.join(", ")}`
    );
  }

  if (!Object.values(PROMOTION_TYPES).includes(promotionUsage.promotionType)) {
    throw new Error(`Invalid promotion type: ${promotionUsage.promotionType}`);
  }

  if (!Object.values(PROMOTION_STATUS).includes(promotionUsage.status)) {
    throw new Error(`Invalid promotion status: ${promotionUsage.status}`);
  }

  if (promotionUsage.originalAmount < 0) {
    throw new Error("Original amount cannot be negative");
  }

  if (promotionUsage.discountAmount < 0) {
    throw new Error("Discount amount cannot be negative");
  }

  if (promotionUsage.finalAmount < 0) {
    throw new Error("Final amount cannot be negative");
  }

  if (promotionUsage.originalAmount < promotionUsage.discountAmount) {
    throw new Error("Discount amount cannot exceed original amount");
  }

  const expectedFinal =
    promotionUsage.originalAmount - promotionUsage.discountAmount;
  if (Math.abs(promotionUsage.finalAmount - expectedFinal) > 1) {
    // Allow for rounding differences
    throw new Error(
      "Final amount does not match original amount minus discount"
    );
  }

  return true;
};

export const validateFirstLessonSettings = (settings) => {
  if (typeof settings !== "object" || settings === null) {
    throw new Error("First lesson settings must be an object");
  }

  if (typeof settings.enabled !== "boolean") {
    throw new Error(
      "First lesson settings must have an 'enabled' boolean field"
    );
  }

  if (settings.customDiscount) {
    if (typeof settings.customDiscount !== "object") {
      throw new Error("Custom discount must be an object");
    }

    const discount = settings.customDiscount;

    if (!Object.values(PROMOTION_TYPES).includes(discount.type)) {
      throw new Error(
        `Invalid promotion type in custom discount: ${discount.type}`
      );
    }

    if (typeof discount.value !== "number") {
      throw new Error("Custom discount value must be a number");
    }

    if (
      discount.type === PROMOTION_TYPES.PERCENTAGE_DISCOUNT ||
      discount.type === PROMOTION_TYPES.FIRST_LESSON_DISCOUNT
    ) {
      if (discount.value < 0 || discount.value > 100) {
        throw new Error("Percentage discount value must be between 0 and 100");
      }
    }

    if (discount.type === PROMOTION_TYPES.FIXED_AMOUNT_DISCOUNT) {
      if (discount.value < 0) {
        throw new Error("Fixed amount discount value must be non-negative");
      }
    }

    if (typeof discount.enabled !== "boolean") {
      throw new Error("Custom discount must have an 'enabled' boolean field");
    }
  }

  if (
    settings.customDescription &&
    typeof settings.customDescription !== "string"
  ) {
    throw new Error("Custom description must be a string");
  }

  return true;
};

export const validateUserPromotions = (userPromotions) => {
  if (typeof userPromotions !== "object" || userPromotions === null) {
    throw new Error("User promotions must be an object");
  }

  if (typeof userPromotions.firstLessonUsed !== "boolean") {
    throw new Error("firstLessonUsed must be a boolean");
  }

  if (
    typeof userPromotions.totalPromotionsUsed !== "number" ||
    userPromotions.totalPromotionsUsed < 0
  ) {
    throw new Error("totalPromotionsUsed must be a non-negative number");
  }

  if (
    userPromotions.promotionHistory &&
    !Array.isArray(userPromotions.promotionHistory)
  ) {
    throw new Error("promotionHistory must be an array");
  }

  return true;
};

// Notification validation functions
export const validateNotificationPreferences = (preferences) => {
  if (typeof preferences !== "object" || preferences === null) {
    throw new Error("Notification preferences must be an object");
  }

  // Validate email preferences
  if (preferences.email) {
    if (typeof preferences.email !== "object") {
      throw new Error("Email preferences must be an object");
    }

    // Check each notification type is a boolean
    Object.entries(preferences.email).forEach(([type, enabled]) => {
      if (typeof enabled !== "boolean") {
        throw new Error(`Email preference for ${type} must be a boolean`);
      }

      if (!Object.values(NOTIFICATION_TYPES).includes(type)) {
        throw new Error(`Invalid notification type: ${type}`);
      }
    });
  }

  // Validate frequency
  if (
    preferences.frequency &&
    !Object.values(NOTIFICATION_FREQUENCIES).includes(preferences.frequency)
  ) {
    throw new Error(`Invalid notification frequency: ${preferences.frequency}`);
  }

  // Validate timezone
  if (preferences.timezone && typeof preferences.timezone !== "string") {
    throw new Error("Timezone must be a string");
  }

  // Validate quiet hours
  if (preferences.quietHours) {
    const { quietHours } = preferences;

    if (typeof quietHours !== "object") {
      throw new Error("Quiet hours must be an object");
    }

    if (typeof quietHours.enabled !== "boolean") {
      throw new Error("Quiet hours enabled must be a boolean");
    }

    if (quietHours.enabled) {
      if (
        typeof quietHours.start !== "string" ||
        !/^\d{2}:\d{2}$/.test(quietHours.start)
      ) {
        throw new Error("Quiet hours start time must be in HH:MM format");
      }

      if (
        typeof quietHours.end !== "string" ||
        !/^\d{2}:\d{2}$/.test(quietHours.end)
      ) {
        throw new Error("Quiet hours end time must be in HH:MM format");
      }
    }
  }

  return true;
};

export const validateNotificationQueueItem = (queueItem) => {
  const required = [
    "userId",
    "notificationType",
    "data",
    "scheduledFor",
    "status",
  ];
  const missing = required.filter((field) => !queueItem[field]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required notification queue fields: ${missing.join(", ")}`
    );
  }

  if (!Object.values(NOTIFICATION_TYPES).includes(queueItem.notificationType)) {
    throw new Error(`Invalid notification type: ${queueItem.notificationType}`);
  }

  if (!Object.values(NOTIFICATION_QUEUE_STATUS).includes(queueItem.status)) {
    throw new Error(`Invalid queue status: ${queueItem.status}`);
  }

  if (
    queueItem.frequency &&
    !Object.values(NOTIFICATION_FREQUENCIES).includes(queueItem.frequency)
  ) {
    throw new Error(`Invalid notification frequency: ${queueItem.frequency}`);
  }

  if (
    typeof queueItem.attempts !== "undefined" &&
    (typeof queueItem.attempts !== "number" || queueItem.attempts < 0)
  ) {
    throw new Error("Attempts must be a non-negative number");
  }

  return true;
};

export const validateNotificationHistory = (historyItem) => {
  const required = [
    "userId",
    "notificationType",
    "data",
    "toEmail",
    "status",
    "sentAt",
  ];
  const missing = required.filter((field) => !historyItem[field]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required notification history fields: ${missing.join(", ")}`
    );
  }

  if (
    !Object.values(NOTIFICATION_TYPES).includes(historyItem.notificationType)
  ) {
    throw new Error(
      `Invalid notification type: ${historyItem.notificationType}`
    );
  }

  if (
    !Object.values(NOTIFICATION_DELIVERY_STATUS).includes(historyItem.status)
  ) {
    throw new Error(`Invalid delivery status: ${historyItem.status}`);
  }

  if (
    typeof historyItem.toEmail !== "string" ||
    !historyItem.toEmail.includes("@")
  ) {
    throw new Error("toEmail must be a valid email address");
  }

  return true;
};

// Reminder validation functions
export const validateScheduledReminder = (reminder) => {
  const required = [
    "lessonId",
    "studentId",
    "reminderType",
    "scheduledFor",
    "status",
  ];
  const missing = required.filter((field) => !reminder[field]);

  if (missing.length > 0) {
    throw new Error(`Missing required reminder fields: ${missing.join(", ")}`);
  }

  if (!Object.values(NOTIFICATION_TYPES).includes(reminder.reminderType)) {
    throw new Error(`Invalid reminder type: ${reminder.reminderType}`);
  }

  if (!Object.values(REMINDER_STATUS).includes(reminder.status)) {
    throw new Error(`Invalid reminder status: ${reminder.status}`);
  }

  if (
    typeof reminder.attempts !== "undefined" &&
    (typeof reminder.attempts !== "number" || reminder.attempts < 0)
  ) {
    throw new Error("Attempts must be a non-negative number");
  }

  if (
    !(reminder.scheduledFor instanceof Date) &&
    typeof reminder.scheduledFor !== "object"
  ) {
    throw new Error("scheduledFor must be a valid timestamp");
  }

  return true;
};

// Badge validation functions
export const validateBadge = (badge) => {
  const required = ["name", "description", "category", "isActive"];
  const missing = required.filter((field) => !badge[field]);

  if (missing.length > 0) {
    throw new Error(`Missing required badge fields: ${missing.join(", ")}`);
  }

  if (!Object.values(BADGE_CATEGORIES).includes(badge.category)) {
    throw new Error(`Invalid badge category: ${badge.category}`);
  }

  if (typeof badge.isActive !== "boolean") {
    throw new Error("Badge isActive must be a boolean");
  }

  if (typeof badge.name !== "string" || badge.name.trim().length === 0) {
    throw new Error("Badge name must be a non-empty string");
  }

  if (
    typeof badge.description !== "string" ||
    badge.description.trim().length === 0
  ) {
    throw new Error("Badge description must be a non-empty string");
  }

  if (
    badge.sortOrder &&
    (typeof badge.sortOrder !== "number" || badge.sortOrder < 0)
  ) {
    throw new Error("Badge sortOrder must be a non-negative number");
  }

  return true;
};

export const validateUserBadge = (userBadge) => {
  const required = ["userId", "badgeId", "awardedBy", "awardedAt"];
  const missing = required.filter((field) => !userBadge[field]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required user badge fields: ${missing.join(", ")}`
    );
  }

  if (!Object.values(BADGE_AWARD_SOURCES).includes(userBadge.awardedBy)) {
    throw new Error(`Invalid badge award source: ${userBadge.awardedBy}`);
  }

  if (
    typeof userBadge.userId !== "string" ||
    userBadge.userId.trim().length === 0
  ) {
    throw new Error("User badge userId must be a non-empty string");
  }

  if (
    typeof userBadge.badgeId !== "string" ||
    userBadge.badgeId.trim().length === 0
  ) {
    throw new Error("User badge badgeId must be a non-empty string");
  }

  return true;
};

export const validateReview = (review) => {
  const required = ["studentId", "teacherId", "lessonId", "rating"];
  const missing = required.filter((field) => !review[field]);

  if (missing.length > 0) {
    throw new Error(`Missing required review fields: ${missing.join(", ")}`);
  }

  if (
    typeof review.rating !== "number" ||
    review.rating < 1 ||
    review.rating > 5
  ) {
    throw new Error("Review rating must be a number between 1 and 5");
  }

  if (review.comment && typeof review.comment !== "string") {
    throw new Error("Review comment must be a string");
  }

  if (review.badges && !Array.isArray(review.badges)) {
    throw new Error("Review badges must be an array");
  }

  if (
    typeof review.isAnonymous !== "undefined" &&
    typeof review.isAnonymous !== "boolean"
  ) {
    throw new Error("Review isAnonymous must be a boolean");
  }

  if (review.status && !Object.values(REVIEW_STATUS).includes(review.status)) {
    throw new Error(`Invalid review status: ${review.status}`);
  }

  if (
    typeof review.studentId !== "string" ||
    review.studentId.trim().length === 0
  ) {
    throw new Error("Review studentId must be a non-empty string");
  }

  if (
    typeof review.teacherId !== "string" ||
    review.teacherId.trim().length === 0
  ) {
    throw new Error("Review teacherId must be a non-empty string");
  }

  if (
    typeof review.lessonId !== "string" ||
    review.lessonId.trim().length === 0
  ) {
    throw new Error("Review lessonId must be a non-empty string");
  }

  return true;
};

// Discount Code Schema
export const DISCOUNT_CODE_SCHEMA = {
  // Collection: discountCodes
  discountCodes: {
    userId: "string", // User who earned the code
    code: "string", // Unique code (e.g., "REVIEW3-ABC123")
    discountPercentage: "number", // 15
    reviewMilestone: "number", // 3, 6, 9, etc.
    isUsed: "boolean", // false initially
    usedAt: "timestamp", // When code was redeemed
    usedForLessonId: "string", // Lesson ID where used
    expiresAt: "timestamp", // 30 days from creation
    createdAt: "timestamp",
    emailSentAt: "timestamp",
  },
};

// User Review Progress Schema
export const USER_REVIEW_PROGRESS_SCHEMA = {
  // Collection: userReviewProgress
  userReviewProgress: {
    userId: "string", // User ID (document ID)
    totalReviews: "number", // Running count of reviews
    lastRewardAt: "number", // Review count at last reward
    nextRewardAt: "number", // Next milestone (3, 6, 9, etc.)
    codesGenerated: "number", // Total codes generated
    codesUsed: "number", // Total codes redeemed
    lastReviewAt: "timestamp",
    updatedAt: "timestamp",
  },
};

// Validation Functions
export const validateDiscountCode = (discountCode) => {
  const required = [
    "userId",
    "code",
    "discountPercentage",
    "reviewMilestone",
    "isUsed",
    "expiresAt",
  ];
  const missing = required.filter(
    (field) => !discountCode[field] && discountCode[field] !== false
  );

  if (missing.length > 0) {
    throw new Error(
      `Missing required discount code fields: ${missing.join(", ")}`
    );
  }

  if (
    typeof discountCode.userId !== "string" ||
    discountCode.userId.trim().length === 0
  ) {
    throw new Error("Discount code userId must be a non-empty string");
  }

  if (
    typeof discountCode.code !== "string" ||
    discountCode.code.trim().length === 0
  ) {
    throw new Error("Discount code code must be a non-empty string");
  }

  if (
    typeof discountCode.discountPercentage !== "number" ||
    discountCode.discountPercentage <= 0 ||
    discountCode.discountPercentage > 100
  ) {
    throw new Error(
      "Discount code discountPercentage must be a number between 1 and 100"
    );
  }

  if (
    typeof discountCode.reviewMilestone !== "number" ||
    discountCode.reviewMilestone <= 0
  ) {
    throw new Error("Discount code reviewMilestone must be a positive number");
  }

  if (typeof discountCode.isUsed !== "boolean") {
    throw new Error("Discount code isUsed must be a boolean");
  }

  return true;
};

export const validateUserReviewProgress = (progress) => {
  const required = [
    "userId",
    "totalReviews",
    "lastRewardAt",
    "nextRewardAt",
    "codesGenerated",
    "codesUsed",
  ];
  const missing = required.filter(
    (field) => progress[field] === undefined || progress[field] === null
  );

  if (missing.length > 0) {
    throw new Error(
      `Missing required user review progress fields: ${missing.join(", ")}`
    );
  }

  if (
    typeof progress.userId !== "string" ||
    progress.userId.trim().length === 0
  ) {
    throw new Error("User review progress userId must be a non-empty string");
  }

  if (typeof progress.totalReviews !== "number" || progress.totalReviews < 0) {
    throw new Error(
      "User review progress totalReviews must be a non-negative number"
    );
  }

  if (typeof progress.lastRewardAt !== "number" || progress.lastRewardAt < 0) {
    throw new Error(
      "User review progress lastRewardAt must be a non-negative number"
    );
  }

  if (typeof progress.nextRewardAt !== "number" || progress.nextRewardAt <= 0) {
    throw new Error(
      "User review progress nextRewardAt must be a positive number"
    );
  }

  if (
    typeof progress.codesGenerated !== "number" ||
    progress.codesGenerated < 0
  ) {
    throw new Error(
      "User review progress codesGenerated must be a non-negative number"
    );
  }

  if (typeof progress.codesUsed !== "number" || progress.codesUsed < 0) {
    throw new Error(
      "User review progress codesUsed must be a non-negative number"
    );
  }

  return true;
};

export default {
  COLLECTIONS,
  PAYMENT_SCHEMA,
  NOTIFICATION_SCHEMA,
  BADGE_SCHEMA,
  DISCOUNT_CODE_SCHEMA,
  USER_REVIEW_PROGRESS_SCHEMA,
  PAYMENT_STATUS,
  REFUND_STATUS,
  STRIPE_ACCOUNT_STATUS,
  LESSON_PAYMENT_STATUS,
  ONBOARDING_STATUS,
  SKILL_LEVELS,
  MUSICAL_GOALS,
  BUDGET_RANGES,
  AVAILABLE_HOURS,
  PROMOTION_TYPES,
  PROMOTION_STATUS,
  NOTIFICATION_TYPES,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_FREQUENCIES,
  NOTIFICATION_QUEUE_STATUS,
  NOTIFICATION_DELIVERY_STATUS,
  REMINDER_STATUS,
  BADGE_CATEGORIES,
  REVIEW_STATUS,
  BADGE_AWARD_SOURCES,
  validatePaymentData,
  validateStripeAccount,
  validateStudentProfile,
  validatePromotionUsage,
  validateFirstLessonSettings,
  validateUserPromotions,
  validateNotificationPreferences,
  validateNotificationQueueItem,
  validateNotificationHistory,
  validateScheduledReminder,
  validateBadge,
  validateUserBadge,
  validateReview,
  validateDiscountCode,
  validateUserReviewProgress,
};
